import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  startWith,
  switchMap,
} from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filterNil } from '@ngneat/elf';
import { SpaceRepository } from '../state/space.repository';
import { isBoardItem } from '../sessions/common/y-session';
import { YSessionBoardItemsAdapter } from '../sessions/common/y-session-board-items-adapter.service';
import { ItemModel } from '../models/itemModel';
import { CpuMonitoringService, PerformanceTier } from './cpu-monitoring.service';
import { FLAGS, FlagsService } from './flags.service';

@Injectable({
  providedIn: 'root',
})
@UntilDestroy()
export class WebViewerPerformanceControlService {
  private activeWebViewerCanvasId$ = new BehaviorSubject<string>('');
  private hasMoreThanOneWebViewer$ = new BehaviorSubject<boolean>(false);

  constructor(
    private cpuMonitoringService: CpuMonitoringService,
    private flagsService: FlagsService,
    private spaceRepo: SpaceRepository,
    private ySessionBoardItemsAdapter: YSessionBoardItemsAdapter,
  ) {
    this.selectedBoardItemsCountByItemModalType$(ItemModel.WebViewer)
      .pipe(
        map((count) => count > 1),
        distinctUntilChanged(),
        untilDestroyed(this),
      )
      .subscribe(this.hasMoreThanOneWebViewer$);
  }

  shouldPauseWebViewer$(id: string): Observable<boolean> {
    return combineLatest([
      this.flagsService.featureFlagChanged(FLAGS.ONLY_ONE_WEBVIEWER_TIER_2, true),
      this.hasMoreThanOneWebViewer$,
      this.cpuMonitoringService.currentTier$.pipe(
        startWith(this.cpuMonitoringService.currentTierValue),
      ),
      this.activeWebViewerCanvasId$,
    ]).pipe(
      map(
        ([onlyOneWebViewerFlag, hasMoreThanOneWV, tier, canvasItemId]) =>
          onlyOneWebViewerFlag &&
          hasMoreThanOneWV &&
          tier !== PerformanceTier.tierOne &&
          canvasItemId !== id,
      ),
      distinctUntilChanged(),
    );
  }

  setActiveWebViewerCanvasItemId(canvasItemId: string) {
    this.activeWebViewerCanvasId$.next(canvasItemId);
  }

  public selectedBoardItemsCountByItemModalType$(itemModel: ItemModel) {
    return this.spaceRepo.activeSpaceSelectedBoardUid$
      .pipe(filterNil(), distinctUntilChanged())
      .pipe(
        switchMap((boardId) =>
          this.ySessionBoardItemsAdapter
            .boardItems$(this.spaceRepo.activeSpaceId ?? '', boardId)
            .pipe(
              map(
                (items) =>
                  items.objects.filter((item) => isBoardItem(item) && item.type === itemModel)
                    .length,
              ),
            ),
        ),
        distinctUntilChanged(),
      );
  }
}
